import { useEffect, useState } from 'react'
import { isDemoLoaded, loadDemoData, clearDemoData } from '../lib/demoData.js'
import DemoWalkthroughModal from './DemoWalkthroughModal.jsx'

// Demo data section — lives inside SettingsScreen.
//
// New tenants land in an empty app and have no idea what a populated
// dashboard looks like. This section gives them a one-click "show me
// what this looks like with data" button. Inserts a small but
// coherent BBQ-joint dataset (6 menu items + 7 inventory items + 6
// recipes + 14 days of item_sales). Everything is suffixed " (demo)"
// so it's easy to spot and remove.
//
// Once the manager understands the app, they tap "Clear demo data"
// and start adding real items, or just leave it and import their
// real data alongside (the demo rows won't conflict with anything).
export default function DemoDataSection({ restaurantId, onChanged }) {
  const [loaded, setLoaded]   = useState(false)
  const [busy, setBusy]       = useState(false)
  const [checking, setChecking] = useState(true)
  const [error, setError]     = useState(null)
  const [info, setInfo]       = useState(null)
  const [tourOpen, setTourOpen] = useState(false)

  // Check whether demo data is already in this tenant.
  useEffect(() => {
    let mounted = true
    if (!restaurantId) {
      setChecking(false)
      return
    }
    isDemoLoaded(restaurantId).then((ok) => {
      if (!mounted) return
      setLoaded(ok)
      setChecking(false)
    })
    return () => { mounted = false }
  }, [restaurantId])

  async function handleLoad() {
    if (busy || !restaurantId) return
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      const r = await loadDemoData(restaurantId)
      if (r.alreadyLoaded) {
        setInfo('Demo data was already loaded.')
      } else {
        setInfo(`Loaded ${r.items} items and ${r.sales} days of sales rows. Refresh other tabs to see them.`)
      }
      setLoaded(true)
      onChanged?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[DemoDataSection] load failed', e)
      setError(`Could not load demo data: ${e?.message || 'try again.'}`)
    } finally {
      setBusy(false)
    }
  }

  async function handleClear() {
    if (busy || !restaurantId) return
    if (!window.confirm('Remove all demo data from this restaurant? Your real items, recipes, and sales are not touched.')) return
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      const r = await clearDemoData(restaurantId)
      setInfo(`Removed ${r.removed} demo items and their related rows.`)
      setLoaded(false)
      onChanged?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[DemoDataSection] clear failed', e)
      setError(`Could not clear demo data: ${e?.message || 'try again.'}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="mt-6 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
      <div className="text-xs font-medium text-black/55">
        Demo data
      </div>
      <p className="mt-1 text-xs text-black/60 leading-snug">
        New here? Load a small BBQ-joint dataset (menu items, recipes, two weeks of sales)
        so you can see what every screen looks like with real data. Clear it any time —
        nothing else is touched.
      </p>

      {error && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}
      {info && (
        <div className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          {info}
        </div>
      )}

      <div className="mt-3 flex gap-2">
        {!loaded ? (
          <button
            type="button"
            onClick={handleLoad}
            disabled={busy || checking || !restaurantId}
            className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Loading…' : '✨ Load demo data'}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleClear}
            disabled={busy}
            className="flex-1 rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            {busy ? 'Removing…' : 'Clear demo data'}
          </button>
        )}
        <button
          type="button"
          onClick={() => setTourOpen(true)}
          className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
          title="See what to show on each tab"
        >
          📖 Demo guide
        </button>
      </div>

      {loaded && !busy && (
        <div className="mt-2 text-[11px] text-black/55 leading-snug">
          Demo content is tagged{' '}
          <span className="rounded bg-black/5 px-1 font-mono">(demo)</span>
          {' '}in item names so you can tell it apart from real data.
        </div>
      )}

      {tourOpen && <DemoWalkthroughModal onClose={() => setTourOpen(false)} />}
    </section>
  )
}
